// React imports
import {useEffect, useMemo, useState} from 'react';

// @mui material components
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import {Card, Box, Chip, CircularProgress} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';

// Material Dashboard 2 React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import ProfileInfoCard from 'examples/Cards/InfoCards/ProfileInfoCard';
import DataTable from 'examples/Tables/DataTable';

// Overview page components
import Header from 'layouts/profile/components/Header';

// Redux Toolkit
import {useSelector} from 'react-redux';

import axios from 'axios';
import {useNavigate, useParams} from 'react-router-dom';
import {ENDPOINT} from 'constants';

// ProfileInfoCard's `info` prop only accepts strings, so booleans need
// converting to a readable label before being passed in.
const formatEnabled = value =>
  value === undefined ? 'N/A' : value ? 'Enabled' : 'Disabled';

const ORDER_STATUS_COLORS = {
  pending: 'warning', completed: 'success', failed: 'error',
  on_hold: 'info', in_process: 'info',
};
const PAYMENT_STATUS_COLORS = {
  pending: 'warning', successful: 'success', succeeded: 'success', failed: 'error',
};

const purchaseColumns = [
  {Header: 'Order #', accessor: 'orderNumber', width: '18%', align: 'left'},
  {Header: 'Date', accessor: 'date', width: '15%', align: 'left'},
  {Header: 'Order Status', accessor: 'orderStatus', width: '15%', align: 'center'},
  {Header: 'Payment', accessor: 'paymentStatus', width: '15%', align: 'center'},
  {Header: 'Subscription', accessor: 'subscription', width: '19%', align: 'center'},
  {Header: 'Amount', accessor: 'amount', width: '18%', align: 'right'},
];

// Same label logic as the Orders list's Subscription Type column — what was actually
// purchased ("3 Month Plan", "One-time"), not just a raw subscription count, checking both
// item sources (`items` for products/plans, `valuePackItems` for value packs).
const getOrderSubscriptionLabel = order => {
  const items = order.items || [];
  const valuePackItems = order.valuePackItems || [];

  const recurringItem = items.find(item => item.billingType === 'recurring');
  if (recurringItem) return recurringItem.duration?.name || 'Subscription';

  const recurringValuePack = valuePackItems.find(item => item.billingType === 'recurring');
  if (recurringValuePack) {
    return (
      recurringValuePack.snapshots?.completeTier?.Duration?.name ||
      recurringValuePack.tier?.duration?.displayText ||
      'Subscription'
    );
  }

  return null;
};

const getPurchaseRow = order => ({
  // Kept off the rendered columns — used by the row-level click handler to navigate
  _order: order,

  orderNumber: (
    <MDTypography variant="button" fontWeight="bold" color="secondary" title="View Order Details">
      {order.orderNumber || `${order.id?.slice(0, 8)}...`}
    </MDTypography>
  ),
  date: (
    <MDTypography variant="caption" fontWeight="medium">
      {order.dates?.createdAt ? new Date(order.dates.createdAt).toLocaleDateString() : '-'}
    </MDTypography>
  ),
  orderStatus: (
    <MDBadge
      badgeContent={order.status?.order}
      color={ORDER_STATUS_COLORS[order.status?.order] || 'info'}
      variant="gradient"
      size="sm"
      sx={{textTransform: 'capitalize'}}
    />
  ),
  paymentStatus: (
    <MDBadge
      badgeContent={order.status?.payment}
      color={PAYMENT_STATUS_COLORS[order.status?.payment] || 'info'}
      variant="gradient"
      size="sm"
      sx={{textTransform: 'capitalize'}}
    />
  ),
  subscription: order.subscriptions?.length ? (
    <Chip
      label={getOrderSubscriptionLabel(order) || `${order.subscriptions.length} subscription${order.subscriptions.length > 1 ? 's' : ''}`}
      size="small"
      color="info"
      icon={<AutorenewIcon sx={{fontSize: '0.9rem !important'}} />}
      sx={{fontWeight: 600}}
    />
  ) : (
    <Chip
      label="One-time"
      size="small"
      variant="outlined"
      sx={{fontWeight: 600, color: 'text.secondary', borderColor: 'rgba(0,0,0,0.15)'}}
    />
  ),
  amount: (
    <MDTypography variant="button" fontWeight="bold">
      {order.summary?.currency?.toUpperCase()} {parseFloat(order.summary?.finalTotal || 0).toFixed(2)}
    </MDTypography>
  ),
});

// Small stat tile — colored icon square, label, big number (matches the Orders Overview style)
const PurchaseStatTile = ({icon, iconBg, label, value}) => (
  <Grid item xs={12} sm={4}>
    <Card sx={{p: 2.5, height: '100%', borderRadius: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2, bgcolor: iconBg, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
          {icon}
        </Box>
        <MDTypography variant="button" color="text" fontWeight="regular">
          {label}
        </MDTypography>
      </Box>
      <Divider sx={{mb: 1.5}} />
      <MDTypography variant="h4" fontWeight="bold">
        {value}
      </MDTypography>
    </Card>
  </Grid>
);

const CustomerDetails = () => {
  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const {customerId} = useParams();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // Fetch all customers
  const fetchCustomer = async () => {
    try {
      const {data} = await axios.get(
        `${ENDPOINT}api/v1/user/users/${customerId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      setCustomer(data.data.user);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // No admin endpoint filters orders by userId, so the full admin order list is
  // fetched once (same as the Orders page) and filtered client-side by customer id.
  const fetchOrders = async () => {
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/order/allOrdersForAdmin`, {
        headers: {Authorization: `Bearer ${user?.accessToken}`},
      });
      setOrders(data.data?.orders || data.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Call fetch on component mount
  useEffect(() => {
    fetchCustomer();
    fetchOrders();
  }, []);

  const customerOrders = useMemo(
    () => orders.filter(o => o.user?.id === customerId),
    [orders, customerId],
  );

  const subscriptionCount = useMemo(
    () => customerOrders.reduce((sum, o) => sum + (o.subscriptions?.length || 0), 0),
    [customerOrders],
  );

  const activeSubscriptionCount = useMemo(
    () =>
      customerOrders.reduce(
        (sum, o) => sum + (o.subscriptions?.filter(s => s.status === 'active').length || 0),
        0,
      ),
    [customerOrders],
  );

  // Memoized so this array's reference only changes when the actual order list does —
  // an unmemoized `.map()` here would give react-table's pagination a "new" data array on
  // every render, silently resetting it to page 1 (same bug fixed on the Orders/Customers
  // list pages).
  const purchaseRows = useMemo(
    () => customerOrders.map(order => getPurchaseRow(order)),
    [customerOrders],
  );

  const goToOrderDetails = rowData => navigate(`/admin/orders/${rowData._order.id}`);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header user={customer}>
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={12} sx={{display: 'flex'}}>
              <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
              <ProfileInfoCard
                title="profile information"
                info={{
                  fullName: `${customer?.firstName} ${customer?.lastName}`,
                  mobile: `${customer?.country?.phoneCode || ''}-${customer?.phoneNumber}`,
                  email: customer?.email,
                  timeZone: `${customer?.timeZone}`,
                }}
                social={[]}
                action={{
                  route: `/customers/edit/${customer?.id}`,
                  tooltip: 'Edit Profile',
                }}
                shadow={false}
              />

              <ProfileInfoCard
                title="business information"
                info={{
                  businessName: `${customer?.businessDetails?.businessName}`,
                  businessCategory: `${customer?.businessDetails?.businessCategory}`,
                  mobile: `${customer?.businessDetails?.businessContactNumber}`,
                  website: customer?.businessDetails?.websiteURL,
                  address: `${customer?.businessDetails?.businessHouseNumber} ${customer?.businessDetails?.businessStreetName}, ${customer?.businessDetails?.businessCity}, ${customer?.businessDetails?.businessState} ${customer?.businessDetails?.businessPostalCode}, ${customer?.businessDetails?.businessCountry}`,
                  city: `${customer?.businessDetails?.businessCity}`,
                  country: `${customer?.businessDetails?.businessCountry}`,
                  postalCode: `${customer?.businessDetails?.businessPostalCode}`,
                  taxNumber: `${customer?.businessDetails?.taxNumber}`,
                }}
                social={[]}
                action={{
                  route: `/customers/edit/${customer?.id}`,
                  tooltip: 'Edit Profile',
                }}
                shadow={false}
              />

              <ProfileInfoCard
                title="email preferences & account settings"
                info={{
                  accountUpdates: formatEnabled(
                    customer?.accountSettings?.emailPreferences?.accountUpdates,
                  ),
                  orderNotifications: formatEnabled(
                    customer?.accountSettings?.emailPreferences
                      ?.orderNotifications,
                  ),
                  specialOffers: formatEnabled(
                    customer?.accountSettings?.emailPreferences?.specialOffers,
                  ),
                  productUpdate: formatEnabled(
                    customer?.accountSettings?.emailPreferences?.productUpdate,
                  ),
                  newFeaturesAndReleases: formatEnabled(
                    customer?.accountSettings?.emailPreferences
                      ?.newFeaturesAndReleases,
                  ),
                  sessionActivities: formatEnabled(
                    customer?.accountSettings?.emailPreferences
                      ?.sessionActivities,
                  ),
                  twoFactorAuthentication: formatEnabled(
                    customer?.accountSettings?.isTwoFactorAuthenticationEnabled,
                  ),
                  loginWithGoogle: formatEnabled(
                    customer?.accountSettings?.isLoginWithGoogleEnabled,
                  ),
                }}
                social={[]}
                action={{
                  route: `/customers/edit/${customer?.id}`,
                  tooltip: 'Edit Profile',
                }}
                shadow={false}
              />
            </Grid>

            {/* Purchase History — all orders placed by this customer, click-through to Order Details */}
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <MDTypography variant="h6" color="white">
                    Purchase History
                  </MDTypography>
                  <MDTypography variant="button" color="white" sx={{opacity: 0.85}}>
                    {customerOrders.length} order{customerOrders.length === 1 ? '' : 's'}
                  </MDTypography>
                </MDBox>

                <MDBox px={3} pt={3}>
                  <Grid container spacing={2} mb={1}>
                    <PurchaseStatTile
                      icon={<ReceiptLongIcon />}
                      iconBg="#1A73E8"
                      label="Total Purchases"
                      value={customerOrders.length}
                    />
                    <PurchaseStatTile
                      icon={<AutorenewIcon />}
                      iconBg="#5E35B1"
                      label="Subscriptions Taken"
                      value={subscriptionCount}
                    />
                    <PurchaseStatTile
                      icon={<CheckCircleIcon />}
                      iconBg="#2E7D32"
                      label="Active Subscriptions"
                      value={activeSubscriptionCount}
                    />
                  </Grid>
                </MDBox>

                {ordersLoading ? (
                  <Grid container justifyContent="center" alignItems="center" sx={{py: 4}}>
                    <CircularProgress size="2.5rem" color="inherit" />
                  </Grid>
                ) : customerOrders.length === 0 ? (
                  <MDBox px={3} pb={3}>
                    <MDTypography variant="body2" color="text">
                      This customer hasn&apos;t placed any orders yet.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox pt={2}>
                    <DataTable
                      table={{
                        columns: purchaseColumns,
                        rows: purchaseRows,
                      }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries
                      noEndBorder
                      hoverEffect
                      onRowClick={goToOrderDetails}
                    />
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </Header>
      <Footer />
    </DashboardLayout>
  );
};

export default CustomerDetails;
